import firebase from 'firebase/app';

interface UserInfo {
  value: string;
  open: boolean;
}

export type Contact = 'email' | 'home' | 'current' | 'none';

export const contactWithDefault = (value: string): Contact => {
  if (
    value !== 'email' &&
    value !== 'home' &&
    value !== 'current' &&
    value !== 'none'
  ) {
    return 'email';
  }
  return value;
};

export const isUserInfo = (target: unknown): target is UserInfo =>
  typeof target === 'object' &&
  target !== null &&
  target['value'] !== undefined &&
  target['open'] !== undefined;

export const hasPrivilege = (
  privilegePeriod: {
    start: firebase.firestore.Timestamp;
    end: firebase.firestore.Timestamp;
  }[]
): boolean => {
  return privilegePeriod.length === 0
    ? false
    : privilegePeriod.some((u) => {
        const endDate = u.end.toDate();
        endDate.setDate(endDate.getDate() + 1);
        return u.start.toDate() <= new Date() && endDate >= new Date();
      });
};

export interface User {
  userID: string;
  isAdmin: boolean;
  identification: string;
  isDead: boolean;

  // display values
  // userInfoTagList
  sei: string;
  mei: string;
  seiKana: string;
  meiKana: string;
  sex: string;
  kyusei: UserInfo;
  kyuseiKana: UserInfo;
  enterYear: string;
  enterUniversityYear: string;
  finalEducation: UserInfo;
  postNumber: UserInfo;
  address: UserInfo;
  phoneNumber: UserInfo;
  faxNumber: UserInfo;
  postNumberHome: UserInfo;
  addressHome: UserInfo;
  phoneNumberHome: UserInfo;
  faxNumberHome: UserInfo;
  contact: Contact;
  email: UserInfo;
  workplace: UserInfo;
  postNumberWorkplace: UserInfo;
  addressWorkplace: UserInfo;
  phoneNumberWorkplace: UserInfo;
  faxNumberWorkplace: UserInfo;
  comment: UserInfo;
  birthday: UserInfo;

  //特権期間
  privilegePeriod: {
    start: firebase.firestore.Timestamp;
    end: firebase.firestore.Timestamp;
  }[];

  //不着か最新情報か
  emailValid: 'invalid' | 'new';
  currentValid: 'invalid' | 'new';
  homeValid: 'invalid' | 'new';
}

export const userInfoWithDefault = (userInfo: Partial<UserInfo>): UserInfo => ({
  value: userInfo.value ?? '',
  open: userInfo.open ?? false,
});

export const userWithDefault = (user: Partial<User>): User => ({
  userID: user.userID ?? '',
  isAdmin: user.isAdmin ?? false,
  identification: user.identification ?? '',
  isDead: user.isDead ?? false,
  sei: user.sei ?? '',
  mei: user.mei ?? '',
  seiKana: user.seiKana ?? '',
  meiKana: user.meiKana ?? '',
  sex: user.sex ?? '',
  kyusei: userInfoWithDefault(user.kyusei ?? {}),
  kyuseiKana: userInfoWithDefault(user.kyuseiKana ?? {}),
  enterUniversityYear: user.enterUniversityYear ?? '',
  enterYear: user.enterYear ?? '',
  finalEducation: userInfoWithDefault(user.finalEducation ?? {}),
  postNumber: userInfoWithDefault(user.postNumber ?? {}),
  address: userInfoWithDefault(user.address ?? {}),
  phoneNumber: userInfoWithDefault(user.phoneNumber ?? {}),
  faxNumber: userInfoWithDefault(user.faxNumber ?? {}),
  postNumberHome: userInfoWithDefault(user.postNumberHome ?? {}),
  addressHome: userInfoWithDefault(user.addressHome ?? {}),
  phoneNumberHome: userInfoWithDefault(user.phoneNumberHome ?? {}),
  faxNumberHome: userInfoWithDefault(user.faxNumberHome ?? {}),
  contact: user.contact ?? 'email',
  email: userInfoWithDefault(user.email ?? {}),
  workplace: userInfoWithDefault(user.workplace ?? {}),
  postNumberWorkplace: userInfoWithDefault(user.postNumberWorkplace ?? {}),
  addressWorkplace: userInfoWithDefault(user.addressWorkplace ?? {}),
  phoneNumberWorkplace: userInfoWithDefault(user.phoneNumberWorkplace ?? {}),
  faxNumberWorkplace: userInfoWithDefault(user.faxNumberWorkplace ?? {}),
  comment: userInfoWithDefault(user.comment ?? {}),
  birthday: userInfoWithDefault(user.birthday ?? {}),
  privilegePeriod: user.privilegePeriod ?? [],
  emailValid: user.emailValid ?? 'new',
  currentValid: user.currentValid ?? 'new',
  homeValid: user.homeValid ?? 'new',
});

export interface Check {
  isCorrect: boolean;
  alert: string;
}

const newCheck = (alert: string): Check => {
  return {
    isCorrect: alert === '',
    alert,
  };
};

export const userInfoTagList: {
  tagKey: keyof User;
  tagName: string;
  check: (input: string) => Check;
}[] = [
  {
    tagKey: 'sei',
    tagName: '姓',
    check: (input: string): Check =>
      newCheck(input.length > 30 ? '30文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'mei',
    tagName: '名',
    check: (input: string): Check =>
      newCheck(input.length > 30 ? '30文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'seiKana',
    tagName: 'せい（よみがな）',
    check: (input: string): Check => {
      const reg = /^[ぁ-んー ]*$/;
      const alert1 = input.length > 30 ? '30文字以内で入力してください。' : '';
      const alert2 =
        reg.exec(input) === null ? 'ひらがなのみで入力してください。' : '';
      return newCheck(`${alert1}${alert2}`);
    },
  },
  {
    tagKey: 'meiKana',
    tagName: 'めい（よみがな）',
    check: (input: string): Check => {
      const reg = /^[ぁ-んー ]*$/;
      const alert1 = input.length > 30 ? '30文字以内で入力してください。' : '';
      const alert2 =
        reg.exec(input) === null ? 'ひらがなのみで入力してください。' : '';
      return newCheck(`${alert1}${alert2}`);
    },
  },
  {
    tagKey: 'sex',
    tagName: '性別',
    check: (input: string): Check =>
      newCheck(input.length > 30 ? '30文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'kyusei',
    tagName: '旧姓',
    check: (input: string): Check =>
      newCheck(input.length > 30 ? '30文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'kyuseiKana',
    tagName: 'きゅうせい（よみがな）',
    check: (input: string): Check => {
      const reg = /^[ぁ-んー ]*$/;
      const alert1 = input.length > 30 ? '30文字以内で入力してください。' : '';
      const alert2 =
        reg.exec(input) === null ? 'ひらがなのみで入力してください。' : '';
      return newCheck(`${alert1}${alert2}`);
    },
  },
  {
    tagKey: 'enterYear',
    tagName: '入会年度',
    check: (input: string) => {
      const reg = /^[0-9]{4}$/;
      return newCheck(
        reg.exec(input) === null ? '半角数字4桁で入力してください。' : ''
      );
    },
  },
  {
    tagKey: 'enterUniversityYear',
    tagName: '入学年度',
    check: (input: string) => {
      const reg = /^[0-9]{4}$/;
      return newCheck(
        reg.exec(input) === null ? '半角数字4桁で入力してください。' : ''
      );
    },
  },
  {
    tagKey: 'finalEducation',
    tagName: '最終学歴',
    check: (input: string) =>
      newCheck(input.length > 100 ? '100文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'postNumber',
    tagName: '現住所郵便番号',
    check: (input: string) => {
      const reg = /^[0-9]{3}-[0-9]{4}$/;
      return newCheck(
        reg.exec(input) === null && input.length > 0
          ? '半角数字3桁(ハイフン)半角数字4桁で入力してください。例: 100-0001　。海外在住でフォーマットが合わない場合は空欄とし、下の現住所の欄にまとめてご記入ください。'
          : ''
      );
    },
  },
  {
    tagKey: 'address',
    tagName: '現住所',
    check: (input: string) =>
      newCheck(input.length > 200 ? '200文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'phoneNumber',
    tagName: '現住所電話番号',
    check: (input: string) => {
      const reg = /^[0-9+-]*$/;
      return newCheck(
        reg.exec(input) === null
          ? '半角数字と半角ハイフン(-)で入力してください。'
          : ''
      );
    },
  },
  {
    tagKey: 'faxNumber',
    tagName: '現住所FAX番号',
    check: (input: string) => {
      const reg = /^[0-9+-]*$/;
      return newCheck(
        reg.exec(input) === null
          ? '半角数字と半角ハイフン(-)で入力してください。'
          : ''
      );
    },
  },
  {
    tagKey: 'postNumberHome',
    tagName: '帰省先郵便番号',
    check: (input: string) => {
      const reg = /^[0-9]{3}-[0-9]{4}$/;
      return newCheck(
        reg.exec(input) === null && input.length > 0
          ? '半角数字3桁(ハイフン)半角数字4桁で入力してください。例: 100-0001　。海外在住でフォーマットが合わない場合は空欄とし、下の現住所の欄にまとめてご記入ください。'
          : ''
      );
    },
  },
  {
    tagKey: 'addressHome',
    tagName: '帰省先住所',
    check: (input: string) =>
      newCheck(input.length > 200 ? '200文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'phoneNumberHome',
    tagName: '帰省先電話番号',
    check: (input: string) => {
      const reg = /^[0-9+-]*$/;
      return newCheck(
        reg.exec(input) === null
          ? '半角数字と半角ハイフン(-)で入力してください。'
          : ''
      );
    },
  },
  {
    tagKey: 'faxNumberHome',
    tagName: '帰省先FAX番号',
    check: (input: string) => {
      const reg = /^[0-9+-]*$/;
      return newCheck(
        reg.exec(input) === null
          ? '半角数字と半角ハイフン(-)で入力してください。'
          : ''
      );
    },
  },
  {
    tagKey: 'email',
    tagName: '連絡用Email',
    check: (input: string) => {
      const reg = /^[\w\-._]+@[\w\-._]+\.[A-Za-z]+$/;
      return newCheck(
        reg.exec(input) === null && input.length > 0
          ? '正しいメールアドレスを入力してください。'
          : ''
      );
    },
  },
  {
    tagKey: 'workplace',
    tagName: '勤務先（通学先）',
    check: (input: string) =>
      newCheck(input.length > 200 ? '200文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'postNumberWorkplace',
    tagName: '勤務先郵便番号',
    check: (input: string) => {
      const reg = /^[0-9]{3}-[0-9]{4}$/;
      return newCheck(
        reg.exec(input) === null && input.length > 0
          ? '半角数字3桁半角ハイフン(-)半角数字4桁で入力してください。例: 100-0001　。海外でフォーマットが合わない場合は空欄とし、下の現住所の欄にまとめてご記入ください。'
          : ''
      );
    },
  },
  {
    tagKey: 'addressWorkplace',
    tagName: '勤務先住所',
    check: (input: string) =>
      newCheck(input.length > 200 ? '200文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'phoneNumberWorkplace',
    tagName: '勤務先電話番号',
    check: (input: string) => {
      const reg = /^[0-9+-]*$/;
      return newCheck(
        reg.exec(input) === null
          ? '半角数字と半角ハイフン(-)で入力してください。'
          : ''
      );
    },
  },
  {
    tagKey: 'faxNumberWorkplace',
    tagName: '勤務先FAX番号',
    check: (input: string) => {
      const reg = /^[0-9+-]*$/;
      return newCheck(
        reg.exec(input) === null
          ? '半角数字と半角ハイフン(-)で入力してください。'
          : ''
      );
    },
  },
  {
    tagKey: 'comment',
    tagName: 'コメント（自由 300文字以内）',
    check: (input: string) =>
      newCheck(input.length > 300 ? '300文字以内で入力してください。' : ''),
  },
  {
    tagKey: 'birthday',
    tagName: '生年月日',
    check: (input: string) => {
      const reg = /^[0-9]{4}\/[0-9]{2}\/[0-9]{2}$/;
      return newCheck(
        reg.exec(input) === null && input.length > 0
          ? '半角数字4桁/2桁/2桁で入力してください。例: 1995/01/01。'
          : ''
      );
    },
  },
];
